import { createSlice } from '@reduxjs/toolkit';

import { setLike } from 'store/likeCount/action';

import { fetchAndAppendToMediaGallery, fetchMediaGallery } from './actions';

import type { MediaGalleryState } from './types';
import type { Review } from 'store/apiTypes';
const initialState: MediaGalleryState = {
  data: {
    offset: 0,
    limit: 30,
    total: 0,
    contents: [] as Review[],
  },
  isFetching: false,
  isAppendFetching: false,
};

const mediaGallerySlice = createSlice({
  name: 'mediaGallery',
  initialState,
  reducers: {
    updateMediaReviewLikeOptimistic: (state, action) => {
      const { reviewId, isLiked } = action.payload;
      const review = state.data.contents.find((review) => review.id === reviewId);

      if (review) {
        review.isLiked = isLiked;
        review.likeCount = isLiked ? review.likeCount + 1 : Math.max(0, review.likeCount - 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaGallery.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchMediaGallery.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isFetching = false;
      })
      .addCase(fetchMediaGallery.rejected, (state, _action) => {
        state.isFetching = false;
      })
      .addCase(setLike.rejected, (state, action) => {
        const review = state.data.contents.find((review) => review.id === action.meta.arg.reviewId);

        if (review) {
          review.isLiked = !action.meta.arg.isLiked;
          review.likeCount = action.meta.arg.isLiked
            ? Math.max(0, review.likeCount - 1)
            : review.likeCount + 1;
        }
      })
      .addCase(fetchAndAppendToMediaGallery.pending, (state) => {
        state.isAppendFetching = true;
      })
      .addCase(fetchAndAppendToMediaGallery.fulfilled, (state, action) => {
        state.isAppendFetching = false;
        const appendedContents = [...state.data.contents];

        action.payload.contents.forEach((payloadReview) => {
          const possiblyMatchingExistingReview = appendedContents.find(
            (alreadyFetchedReview) => payloadReview.id === alreadyFetchedReview.id,
          );

          if (possiblyMatchingExistingReview) {
            possiblyMatchingExistingReview.likeCount = payloadReview.likeCount;
            possiblyMatchingExistingReview.isLiked = payloadReview.isLiked;
            possiblyMatchingExistingReview.media = [
              ...possiblyMatchingExistingReview.media,
              ...payloadReview.media,
            ];
          } else {
            appendedContents.push(payloadReview);
          }
        });

        state.data = {
          ...action.payload,
          contents: appendedContents,
        };
      })
      .addCase(fetchAndAppendToMediaGallery.rejected, (state) => {
        state.isAppendFetching = false;
      });
  },
});

export const { updateMediaReviewLikeOptimistic } = mediaGallerySlice.actions;

export const mediaGalleryReducer = mediaGallerySlice.reducer;
